export const BLOOD_PRODUCTS = [
  'Fresh whole blood',
  'Whole blood',
  'Packed cells',
  'Fresh frozen plasma',
  'Platelets',
  'Cryoprecipitate'
] as const

export const SCREEN_TESTS = ['HBsAg', 'HCV', 'HIV', 'VDRL'] as const
export const CHART_REMARKS = ['No reaction', 'Stable', 'Reaction'] as const
export const ABO_GROUPS = ['O', 'A', 'B', 'AB'] as const
export const RH_TYPES = ['Positive', 'Negative'] as const

export type BloodProduct = (typeof BLOOD_PRODUCTS)[number]
export type Abo = (typeof ABO_GROUPS)[number]
export type Rh = (typeof RH_TYPES)[number]
export type ScreenTest = (typeof SCREEN_TESTS)[number]
export type ScreenResult = 'pending' | 'non-reactive'
export type Screen = Record<ScreenTest, ScreenResult>
export type Serology = 'non-reactive' | 'reactive' | 'pending'
export type UnitStatus = 'available' | 'compatible' | 'incompatible' | 'issued' | 'transfused' | 'returned'
export type ChartRemark = (typeof CHART_REMARKS)[number]

export type TransfusionRequest = {
  id: string
  encounterId: string
  patientName: string
  product: BloodProduct
  units: number
  pcv: number
  indication: string
  note: string
  abo: Abo
  rh: Rh
  screen: Screen
  createdAt: string
}

export type Administration = {
  id: string
  startedAt: string
  endedAt: string
  remark: ChartRemark
  note: string
  steps: string[]
  returnedToBank: boolean
}

export type BloodUnit = {
  id: string
  unitNumber: string
  product: BloodProduct
  abo: Abo
  rh: Rh
  collectionDate: string
  expiry: string
  serology: Serology
  status: UnitStatus
  crossmatch?: 'compatible' | 'incompatible'
  transfusionRequestId?: string
  administration?: Administration
}

export type NewTransfusionRequest = {
  product: BloodProduct
  units: number
  pcv: number
  indication: string
  note: string
  abo: Abo
  rh: Rh
  screen: Screen
}

export type NewBloodUnit = {
  unitNumber: string
  product: BloodProduct
  abo: Abo
  rh: Rh
  collectionDate: string
  expiry: string
  serology: Serology
}

export const groupLabel = (abo: Abo, rh: Rh) => `${abo}${rh === 'Positive' ? '+ve' : '−ve'}`

export const isExpired = (expiry: string, now = new Date()) => {
  const [y, m, d] = expiry.split('-').map(Number)
  if (!y || !m || !d) return true
  const end = new Date(y, m - 1, d, 23, 59, 59, 999)
  return now.getTime() > end.getTime()
}

export const blankScreen = (): Screen => ({
  HCV: 'non-reactive',
  HBsAg: 'non-reactive',
  HIV: 'non-reactive',
  VDRL: 'non-reactive'
})

export function apiErrorMessage(error: unknown) {
  const errors = (error as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors
  if (Array.isArray(errors)) {
    const messages = errors
      .map(item => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object' && 'detail' in item)
          return String((item as { detail?: string }).detail || '')
        return ''
      })
      .filter(Boolean)
    if (messages.length) return messages.join(' ')
  }
  return 'Something went wrong.'
}
